import { useEffect, useState } from "react";
import { Box, Collapse, Table, TableBody } from "@mui/material";
import { useTranslation } from "react-i18next";
import SearchIcon from "@mui/icons-material/Search";

import { ShortOrderWorkOfferSubcategory } from "./ShortOrderWorkOfferSubcategory";
import { ShortOrderWorkOffersHeader } from "./ShortOrderWorkOffersHeader";
import { ShortOrderWorkOfferRow } from "./ShortOrderWorkOfferRow";
import { RoundedButton } from "../../../../common/components/styled/styled";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import {
  TALENT_OFFER_ACCEPTED,
  TALENT_OFFER_DECLINED,
  TALENT_OFFER_OFFERED,
} from "../../constants";
import {
  cancelWorkOffers,
  getWorkOffersState,
  selectWorkOffersByBundleId,
} from "../../workOffersSlice";
import OverlaySpinner from "../../../../common/components/functional/overlaySpinner/OverlaySpinner";
import useIsLoading from "../../../../hooks/useIsLoading";
import { sortByName } from "../../../../common/helpers/common";

type Props = {
  bundleId: string;
  disableManualOffers: boolean;
  openSearchDialog: () => void;
  isOrderFulfilled: boolean;
};

export const ShortOrderWorkOffers = ({
  bundleId,
  disableManualOffers,
  openSearchDialog,
  isOrderFulfilled,
}: Props) => {
  const { t } = useTranslation("order");
  const dispatch = useAppDispatch();
  const [openAutomatch, setOpenAutomatch] = useState(true);
  const [openManual, setOpenManual] = useState(false);

  const workOffers = useAppSelector((state) =>
    selectWorkOffersByBundleId(state, bundleId),
  )!!;

  const isLoading = useIsLoading(["workOffers"]);
  const [offersToCancel, setOffersToCancel] = useState<string[]>([]);

  const setOfferToCancel = (talentId: string) => {
    if (offersToCancel.includes(talentId)) {
      setOffersToCancel(offersToCancel.filter((id) => id !== talentId));
    } else {
      setOffersToCancel([...offersToCancel, talentId]);
    }
  };

  const cancelOffers = () => {
    dispatch(
      cancelWorkOffers({
        talentIds: offersToCancel,
        bundleId,
      }),
    );
    setOffersToCancel([]);
  };

  useEffect(() => {
    if (!bundleId) return;
    dispatch(getWorkOffersState({ bundleId }));
  }, [dispatch, bundleId]);

  const automatched = workOffers?.automatched
    ? sortByName([...workOffers.automatched])
    : [];

  const manuallyOffered = workOffers?.manuallyOffered
    ? sortByName([...workOffers.manuallyOffered])
    : [];

  return (
    <Box sx={{ width: "50%" }}>
      <Box sx={{ backgroundColor: "white", borderRadius: "8px" }}>
        <OverlaySpinner isLoading={isLoading} />
        <ShortOrderWorkOffersHeader
          title={t("automatching")}
          open={openAutomatch}
          setOpen={() => setOpenAutomatch(!openAutomatch)}
          hideCategoryNames={false}
        />
        <Collapse in={openAutomatch}>
          <ShortOrderWorkOfferSubcategory
            categoryName={t("noAnswer")}
            workOffers={automatched.filter(
              (offer) => offer.workOfferStatus === TALENT_OFFER_OFFERED,
            )}
            isOrderFulfilled={isOrderFulfilled}
          />
          <ShortOrderWorkOfferSubcategory
            categoryName={t("declined")}
            workOffers={automatched.filter(
              (offer) => offer.workOfferStatus === TALENT_OFFER_DECLINED,
            )}
            isOrderFulfilled={isOrderFulfilled}
          />
          <ShortOrderWorkOfferSubcategory
            categoryName={t("accepted")}
            workOffers={automatched.filter(
              (offer) => offer.workOfferStatus === TALENT_OFFER_ACCEPTED,
            )}
            isOrderFulfilled={isOrderFulfilled}
          />
        </Collapse>
        <ShortOrderWorkOffersHeader
          title={t("manualOffers")}
          open={openManual}
          setOpen={() => setOpenManual(!openManual)}
          hideCategoryNames={true}
        />
        <Collapse in={openManual}>
          <Table size="small" sx={{ padding: 0, margin: 0 }}>
            <TableBody>
              {manuallyOffered.map((offer, i) => (
                <ShortOrderWorkOfferRow
                  key={i}
                  talentWorkOffer={offer}
                  selected={offersToCancel.includes(offer.id)}
                  onSelect={setOfferToCancel}
                  sx={
                    i % 2 === 0
                      ? {
                          backgroundColor: "#FAFAFB",
                        }
                      : {
                          backgroundColor: "#FFFFFF",
                        }
                  }
                  isOrderFulfilled={isOrderFulfilled}
                />
              ))}
            </TableBody>
          </Table>
        </Collapse>
        {offersToCancel?.length > 0 ? (
          <Box m={2}>
            <RoundedButton
              sx={{ marginBottom: 2 }}
              variant="outlined"
              onClick={cancelOffers}
            >
              {t("order:cancelShiftOffer")}
            </RoundedButton>
          </Box>
        ) : null}
      </Box>
      {/* Disable manual offer option if order row is in automatching */}
      <Box>
        <RoundedButton
          variant="outlined"
          onClick={openSearchDialog}
          sx={{ marginTop: 2 }}
          disabled={disableManualOffers}
        >
          <SearchIcon />
          {t("searchTalentsManually")}
        </RoundedButton>
      </Box>
    </Box>
  );
};
